import Box from '@mui/material/Box';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    loadActivationSticks,
    loadOnboardDevices,
    loadOnboardDevicesOnlinePeers,
    loadSystemConfigurations,
    selectActivationSticks,
    selectOnboardDevices,
    selectOnboardDevicesError,
    selectOnboardDevicesLoading,
    selectOnboardDevicesOnlinePeers,
    selectOnboardDevicesPagination,
} from '../store/devices.reducer';
import { OnboardDevicesTableGrid } from '../Components/OnboardDevicesTableGrid';
import {
    Device,
    DeviceWithSticks,
    OnboardDeviceConnectionStatus,
    OnboardDevicesQueryParams,
} from '../interfaces';
import { useOnboardDevicesStatusWebSocket } from '../hooks/useOnboardDevicesStatusWebSocket';
import {
    buildInitialOnboardDeviceStatusMap,
    ONBOARD_DEVICE_STATUS_SOURCE_SERVER,
} from '../utils/onboardDeviceOnlineStatus';
import { DEFAULT_ONBOARD_DEVICES_PAGE_SIZE } from '../api/getOnboardDevices';

export const OnboardDevices = () => {
    const dispatch = useAppDispatch();
    const onboardDevices = useAppSelector(selectOnboardDevices);
    const onboardDevicesPagination = useAppSelector(selectOnboardDevicesPagination);
    const onboardDevicesLoading = useAppSelector(selectOnboardDevicesLoading);
    const onboardDevicesError = useAppSelector(selectOnboardDevicesError);
    const activationSticks = useAppSelector(selectActivationSticks);
    const peersOnline = useAppSelector(selectOnboardDevicesOnlinePeers);
    const [onboardDevicesQuery, setOnboardDevicesQuery] = useState<OnboardDevicesQueryParams>({
        page: 1,
        pageSize: DEFAULT_ONBOARD_DEVICES_PAGE_SIZE,
        sortBy: 'createdAt',
        sortOrder: 'desc',
    });
    const [onlineStatusByDeviceId, setOnlineStatusByDeviceId] = useState<
        Record<string, OnboardDeviceConnectionStatus>
    >({});

    useEffect(() => {
        dispatch(loadActivationSticks());
        dispatch(loadSystemConfigurations());
        dispatch(loadOnboardDevicesOnlinePeers());
    }, [dispatch]);

    useEffect(() => {
        const promise = dispatch(loadOnboardDevices(onboardDevicesQuery));

        return () => {
            promise.abort();
        };
    }, [dispatch, onboardDevicesQuery]);

    const handleOnboardDevicesQueryChange = useCallback((queryPatch: Partial<OnboardDevicesQueryParams>) => {
        setOnboardDevicesQuery((previousQuery) => ({
            ...previousQuery,
            ...queryPatch,
        }));
    }, []);

    const onboardDevicesWithSticks: DeviceWithSticks[] | null = useMemo(() => {
        if (onboardDevices) {
            const sticks = activationSticks || [];

            return onboardDevices.map((device: Device) => ({
                ...device,
                activationSticks: sticks.filter((stick) => stick.deviceId === device.deviceId),
            }));
        } else {
            return null;
        }
    }, [onboardDevices, activationSticks]);

    useEffect(() => {
        if (!onboardDevices) {
            return;
        }

        setOnlineStatusByDeviceId(buildInitialOnboardDeviceStatusMap(peersOnline));
    }, [onboardDevices, peersOnline]);

    useOnboardDevicesStatusWebSocket({
        enabled: Boolean(onboardDevicesWithSticks),
        onConnected: () => {
            dispatch(loadOnboardDevicesOnlinePeers());
        },
        onStatusUpdate: (payload) => {
            if (payload.serverType && payload.serverType !== ONBOARD_DEVICE_STATUS_SOURCE_SERVER) {
                return;
            }

            setOnlineStatusByDeviceId((previousState) => {
                const nextState = { ...previousState };

                if (payload.status === 'online') {
                    nextState[payload.deviceId] = 'online';
                } else {
                    delete nextState[payload.deviceId];
                }

                return nextState;
            });
        },
    });

    return (
        <Box sx={{ width: '100%', padding: '20px', height: '100vh' }}>
            <OnboardDevicesTableGrid
                devices={onboardDevicesWithSticks || []}
                pagination={onboardDevicesPagination}
                loading={onboardDevicesLoading}
                error={onboardDevicesError}
                query={onboardDevicesQuery}
                onQueryChange={handleOnboardDevicesQueryChange}
                onlineStatusByDeviceId={onlineStatusByDeviceId}
            />
        </Box>
    );
};
